import { css } from '@emotion/react';
import { ObjectType } from '@weave/schema-gen-ts/dist/apis/api/phone/audits/v1/api.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { SurfaceHeader } from '@frontend/surface-header';
import { Chip, ChipOptional, Text, useTooltip } from '@frontend/design-system';
import { SETTING_TYPE_LABELS } from '../phone-system-history-config';
import { PhoneSystemHistoryTrackingIds } from '../utils/tracking-ids';

type DetailsHeaderProps = {
  title?: string;
  objectType?: ObjectType;
  subTitle?: string;
  description?: string;
  iconName?: IconName;
  children?: React.ReactNode;
  onClose: () => void;
};

export const DetailsHeader: React.FC<DetailsHeaderProps> = ({
  title,
  objectType,
  subTitle,
  description,
  iconName,
  onClose,
  children,
}) => {
  const { t } = useTranslation('phone');
  const typeConfig = objectType ? SETTING_TYPE_LABELS[objectType] : undefined;
  const resolvedIconName = iconName ?? typeConfig?.iconName;
  const resolvedSubTitle = subTitle ?? typeConfig?.label;
  const resolvedDescription = description ?? (typeConfig?.label ? typeConfig.description : undefined);

  return (
    <SurfaceHeader.Root noBorder css={surfaceHeaderRootStyle}>
      <SurfaceHeader.Row>
        <SurfaceHeader.Title>{title ?? t('History')}</SurfaceHeader.Title>
        <SurfaceHeader.Actions>
          <SurfaceHeader.Icon
            iconName='x'
            onClick={onClose}
            trackingId={PhoneSystemHistoryTrackingIds.detailsTray.xIconCloseButton}
          />
        </SurfaceHeader.Actions>
      </SurfaceHeader.Row>
      <SurfaceHeader.Body>
        <div css={bodyColumnStyle}>
          <div css={bodyRowStyle}>
            {resolvedIconName && <Icon name={resolvedIconName} color='default' size={16} />}
            {resolvedSubTitle && (
              <Text size='medium' color='light'>
                {resolvedSubTitle}
              </Text>
            )}
          </div>

          {children}

          {resolvedDescription && (
            <Text size='medium' color='light'>
              {resolvedDescription}
            </Text>
          )}
        </div>
      </SurfaceHeader.Body>
    </SurfaceHeader.Root>
  );
};

/**
 * If there are multiple locations, we show a chip with the count of locations and a tooltip that lists all the
 * locations. If there is only one location, we show a chip with the location name. If there are no locations,
 * we show a chip with "Unknown Location".
 *
 */
export const LocationDetails: React.FC<{ locationNames: string[] }> = ({ locationNames }) => {
  const { t } = useTranslation('phone');

  const { Tooltip, tooltipProps, triggerProps } = useTooltip();

  if (locationNames.length > 1) {
    return (
      <>
        <ChipOptional variant={'default'} leftElement={<Icon name='locations' size={16} />} {...triggerProps}>
          {t('{{count}} Locations', {
            count: locationNames.length,
          })}
        </ChipOptional>

        <Tooltip {...tooltipProps}>
          <div css={locationTooltipStyle}>
            {locationNames.map((locationName, index) => (
              <Text key={index} size='medium' color='white'>
                {locationName}
              </Text>
            ))}
          </div>
        </Tooltip>
      </>
    );
  }

  return <Chip.Location>{locationNames[0] ?? t('Unknown Location', { ns: 'phone' })}</Chip.Location>;
};

// --- Styles ---

const surfaceHeaderRootStyle = css`
  padding: 0;
  padding-bottom: var(--fabric-space-gap-md);
  border-bottom: 1px solid var(--fabric-color-border-disabled);
`;

const bodyColumnStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xs);
`;

const bodyRowStyle = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-xs);
`;

const locationTooltipStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xxs);
`;
